"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLanguage } from "@/lib/i18n/context";
import { useResolvedTheme } from "@/lib/hooks/use-resolved-theme";

import { motion } from "framer-motion";

export function Nav({
    vertical = false,
    onItemClick,
    instanceId = "desktop",
}: {
    vertical?: boolean;
    onItemClick?: () => void;
    instanceId?: string;
}) {
    const { t } = useLanguage();
    // usePathname() already re-renders on route change — single source of truth
    const pathname = usePathname();
    const { isLight } = useResolvedTheme();

    const routes = [
        { href: "/", label: t.nav.home },
        { href: "/o-nas", label: t.nav.about },
        { href: "/uslugi", label: t.nav.services },
        { href: "/kontakt", label: t.nav.contact },
    ];

    // Normalise pathname: strip trailing slash except for root "/"
    const normPathname = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;

    return (
        <nav className={`flex ${vertical ? "flex-col gap-8 w-full items-start" : "items-center gap-8"}`}>
            {routes.map((link, idx) => {
                // Active when pathname matches exactly (no trailing-slash mismatch)
                const isActive = normPathname === link.href;

                return (
                    <Link
                        key={link.href}
                        href={link.href}
                        onClick={onItemClick}
                        className={`transition-all duration-500 relative group flex items-baseline
                            ${vertical
                                ? "text-4xl sm:text-5xl font-black uppercase tracking-tighter w-full text-left py-2"
                                : "text-sm font-semibold px-4 py-2 rounded-full justify-center"
                            }
                            ${isActive
                                ? (isLight ? "text-orange-600" : "text-blue-400")
                                : (vertical
                                    ? "text-zinc-400 dark:text-zinc-600 hover:text-zinc-900 dark:hover:text-zinc-100"
                                    : "text-zinc-600 dark:text-zinc-400")
                            }
                            ${!vertical && (isLight ? "hover:text-orange-600" : "hover:text-blue-400")}
                        `}
                    >
                        {vertical && (
                            <span className="text-xs font-mono mr-4 opacity-50 tracking-normal font-medium">
                                0{idx + 1}
                            </span>
                        )}
                        <span className="relative">
                            {link.label}
                            {vertical && isActive && (
                                <motion.div
                                    // instanceId scopes layoutId so desktop and mobile don't interfere
                                    layoutId={`active-indicator-${instanceId}`}
                                    className={`absolute -right-6 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${isLight ? "bg-orange-600" : "bg-blue-400"}`}
                                />
                            )}
                        </span>
                        {!vertical && (
                            <span
                                className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 transition-all duration-300
                                    ${isLight ? "bg-orange-600" : "bg-blue-400"}
                                    ${isActive ? "w-2/3" : "w-0 group-hover:w-2/3"}
                                `}
                            />
                        )}
                    </Link>
                );
            })}
        </nav>
    );
}
